//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"

//FTV Components
import {
  kioskApiGetFTVOption,
  kioskApiPostFTVOptionCreate,
  kioskApiPutFTVOptionUpdate,
} from "../../kioskApi"

//Helpers
import FormikTextInput from "../../../../helpers/formikInputs/FormikTextInput"
import FormikDropDown from "../../../../helpers/formikInputs/FormikDropDown"
import FormikCheckBox from "../../../../helpers/formikInputs/FormikCheckboxInput"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { Badge } from "primereact/badge"

//SOAR Components
import { UserContext } from "../../../../context/userContext"

//Styles
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import myStyles from "../../../../styles/kiosk/KioskAdminFTVOptions.module.css"
import { useCallback, useContext, useEffect, useState } from "react"
import { LogError } from "../../../../helpers/logger"
import { LoaderMedium } from "../../../../components/Loaders"
import KioskAdminFormWizard from "../FormWizard/KioskAdminFormWizard"
import { classNames } from "primereact/utils"
import { Tooltip } from "primereact/tooltip"

export default function FirstTimeVisitorOptionsForm({
  optionId,
  deleteMode,
  kioskOrganizationId,
  organization_id,
  kioskForms,
  onClose,
  onSubmit,
  onNewKioskForm,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [showFormWizard, setShowFormWizard] = useState(false)
  const [formOptions, setFormOptions] = useState(null)
  const [currentForm, setCurrentForm] = useState(null)
  const [optionProfile, setOptionProfile] = useState({
    id: undefined,
    organizationId: kioskOrganizationId,
    name: "",
    description: "",
    formId: undefined,
    active: true,
    userId: userCtx.session.user.staff_id,
  })

  let headerText = "Add Option"

  if (optionId) {
    if (deleteMode) {
      headerText = "Are you sure you want to delete this item?"
    } else {
      headerText = "Edit option"
    }
  }

  useEffect(() => {
    if (!deleteMode) {
      const options = [
        ...kioskForms.filter(
          (form) => form.active || form.id === optionProfile.formId,
        ),
        { id: "new-form", name: "New Form", active: true },
      ]
      setFormOptions(options)

      if (options.length > 0 && optionProfile.formId && !currentForm) {
        const formOption = options.find(
          (form) => form.id === optionProfile.formId,
        )

        if (formOption) {
          setCurrentForm(formOption) //This will either set the new formOption, or clear the currentForm if the selected form is not found
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskForms, deleteMode, optionProfile.formId])

  const loadOption = useCallback(async () => {
    try {
      setIsLoading(true)

      if (optionId) {
        const result = await kioskApiGetFTVOption(
          userCtx.tokens.getKioskToken(),
          optionId,
        )

        if (result) {
          setOptionProfile(result)
          setIsLoading(false)
        } else {
          userCtx.setNotificationError(
            "Unable to load First Time Visitor profile",
          )
          onclose()
        }
      } else {
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
      onclose()
      LogError("Unable to load First Time Visitor profile", error)
      userCtx.setNotificationError("Unable to load First Time Visitor profile")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionId])

  useEffect(() => {
    loadOption()
  }, [loadOption])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Please enter a name for this option"),
    formId: Yup.string().required("Please select a form"),
  })

  const handleSubmit = async (values) => {
    try {
      if (!values.id) {
        await kioskApiPostFTVOptionCreate(
          userCtx.tokens.getKioskToken(),
          values,
        )
      } else if (!deleteMode) {
        await kioskApiPutFTVOptionUpdate(userCtx.tokens.getKioskToken(), values)
      } else {
        const deleteValues = { ...values, deleteOption: true }
        await kioskApiPutFTVOptionUpdate(
          userCtx.tokens.getKioskToken(),
          deleteValues,
        )
      }

      onSubmit()
      return true
    } catch (error) {
      LogError(
        "Unable to submit Kiosk First Time Visitor Option profile to service",
        error,
      )
      userCtx.setNotificationError(
        "Unable to save changes to the Kiosk service",
      )
      return false
    }
  }

  const DropDownItemTemplate = (option, isSelected = false) => {
    if (!option) {
      return <span className={kioskStyles.tableData}> </span>
    } else {
      return (
        <div
          className={classNames(kioskStyles.tableData, {
            [myStyles.inactiveBadgeContainer]: option?.active === false,
            [myStyles.inactiveBadgeContainerSelected]:
              isSelected && option?.active === false,
          })}
        >
          {option?.name || " "}
          {option?.active === false && (
            <Badge value="Inactive" severity="danger"></Badge>
          )}
        </div>
      )
    }
  }

  return (
    <Dialog
      header={headerText}
      visible={true}
      onHide={onClose}
      className={myStyles.dialog}
      closable={true}
    >
      {isLoading ? (
        <LoaderMedium />
      ) : (
        <>
          <Formik
            initialValues={optionProfile}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
              if (await handleSubmit(values)) {
                resetForm({ values })
              }
            }}
          >
            {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
              <Form onSubmit={handleSubmit} autoComplete="off">
                {showFormWizard ? (
                  <KioskAdminFormWizard
                    onClose={() => {
                      setShowFormWizard(false)
                      setFieldValue("formId", undefined)
                    }}
                    onSubmit={(form) => {
                      onNewKioskForm(form)
                      setShowFormWizard(false)
                      setFieldValue("formId", form.id)
                    }}
                    templates={undefined}
                    formId={undefined}
                    kioskOrganizationId={kioskOrganizationId}
                    organizationId={organization_id}
                  />
                ) : null}
                <FormikTextInput
                  label={"Name"}
                  name={"name"}
                  helpText={
                    "This will be displayed as the option text on the Kiosk interface"
                  }
                  helpIcon
                  disabled={deleteMode || isSubmitting}
                />
                <FormikDropDown
                  label={"Form"}
                  name={"formId"}
                  helpText={
                    "This is the form that will need to be completed for the option"
                  }
                  helpIcon
                  options={formOptions}
                  optionLabel={"name"}
                  optionValue={"id"}
                  disabled={deleteMode || isSubmitting}
                  itemTemplate={(option) =>
                    DropDownItemTemplate(option, values?.formId === option?.id)
                  }
                  //The valueTemplate shows the selected item in the closed state of the dropdown, we do not need to check if it's selected here
                  valueTemplate={(option) => DropDownItemTemplate(option)}
                  onChangeCallback={(e) => {
                    if (e === "new-form") {
                      setShowFormWizard(true)
                    } else {
                      //Keep track of the current form option for display purposes
                      const formOption = formOptions?.find(
                        (form) => form.id === e,
                      )

                      setCurrentForm(formOption)

                      if (formOption?.active === false && values.active) {
                        //If the selected form is inactive, set the option to inactive as well
                        setFieldValue("active", false)
                      }
                    }
                  }}
                />
                <FormikTextInput
                  label={"Description"}
                  name={"description"}
                  helpText={"A description of the option"}
                  helpIcon
                  disabled={deleteMode || isSubmitting}
                />

                {!deleteMode ? (
                  <div className={myStyles.enableStatusControls}>
                    <FormikCheckBox
                      name={"active"}
                      label={"Enabled "}
                      helpText={
                        "This field indicates whether the option is active in Kiosk, inactive options will not be displayed in the Kiosk"
                      }
                      helpIcon
                      useSwitch
                      disabled={
                        deleteMode ||
                        isSubmitting ||
                        currentForm?.active === false //Disable if the selected form is explicitly inactive
                      }
                    />
                    {currentForm?.active === false && (
                      <div className={myStyles.inactiveWarningContainer}>
                        <Tooltip
                          target={".tt-inactiveWarning"}
                          className="custom-tooltip"
                        />
                        <i
                          className={`pi pi-exclamation-triangle ${kioskStyles.info} ${myStyles.inactiveIcon} tt-inactiveWarning`}
                          data-pr-tooltip={
                            "To enable this option, please select an active form."
                          }
                          data-pr-position="right"
                        ></i>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className={`${myStyles.buttonGroup}`}>
                  <Button
                    label="Cancel"
                    type="button"
                    onClick={() => {
                      onClose()
                    }}
                    className={`${kioskStyles.invertButton} ${kioskStyles.button}`}
                    disabled={isSubmitting}
                  />
                  <Button
                    label={deleteMode ? "Delete" : "Ok"}
                    type="submit"
                    className={`${kioskStyles.button} ${kioskStyles.primaryButton}`}
                    disabled={isSubmitting}
                    iconPos="left"
                    icon={isSubmitting && "pi pi-spin pi-spinner"}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}
    </Dialog>
  )
}
